import {
  Bank,
  Briefcase,
  Buildings,
  ChatsCircle,
  Code,
  CurrencyDollar,
  Database,
  Factory,
  Globe,
  GraduationCap,
  Handshake,
  Leaf,
  Lightbulb,
  Megaphone,
  Robot,
  Rocket,
  ShieldCheck,
  ShoppingCart,
  Stethoscope,
  TrendUp,
  Wrench,
  type Icon,
} from '@phosphor-icons/react';
import type { BriefTheme } from '@mg/shared';

/**
 * A Phosphor (duotone) icon + the topical accent color used to fill it.
 *
 * The accent is drawn on a card that is white in the light theme and #26262A in the dark one, so
 * keep each new one at 3:1 or more against both (WCAG non-text contrast) and off blue, cyan and
 * teal: the app has no blue UI colour, and the colour-token gate flags hues 170-255.
 */
export interface ThemeIcon {
  Icon: Icon;
  color: string;
}

interface Category extends ThemeIcon {
  re: RegExp;
}

/**
 * Ordered by tiebreak priority (earlier wins on equal score). Each regex runs
 * against lowercased text; short/ambiguous tokens use word boundaries to avoid
 * false hits (e.g. `\bai\b` must not match "campaign"). Specific domains/actions
 * sit above the very generic "AI" so AI-sector themes don't all collapse to a bot.
 */
const CATEGORIES: Category[] = [
  { Icon: Stethoscope, color: '#ef4444', re: /health|medical|medicine|clinic|patient|hospital|orthop|wellness|therap|surg|doctor|nurse|pharma|diagnos|treatment|\bcare\b/ },
  { Icon: CurrencyDollar, color: '#059669', re: /\bfund(s|ing|ed)?\b|invest|capital|venture|raising|\braise\b|valuation|\bipo\b|revenue|finance|financial|profit|econom|earnings/ },
  { Icon: Handshake, color: '#d946ef', re: /partner|collaborat|\bdeals?\b|merger|acquisition|alliance|agreement|joint venture/ },
  { Icon: Code, color: '#a855f7', re: /\bcod(e|es|ing|er)\b|software|develop|programming|\bapi(s)?\b|devops|open source|github|framework|\bsdk\b/ },
  { Icon: ShieldCheck, color: '#6b8e23', re: /security|cyber|privacy|breach|\bprotect|compliance|\brisk|fraud|\bsafety|encryption/ },
  { Icon: Leaf, color: '#16a34a', re: /sustainab|climate|\bgreen\b|renewable|\benergy|carbon|environment|\beco-/ },
  { Icon: GraduationCap, color: '#d97706', re: /education|\blearn|training|\bcourse|student|universit|\bschool|\bskills?\b/ },
  { Icon: Briefcase, color: '#ea580c', re: /career|\bjobs?\b|hir(e|es|ing)|recruit|employ|workforce|talent|workplace|leadership|\bhr\b/ },
  { Icon: ShoppingCart, color: '#db2777', re: /retail|e-?commerce|shopping|\bconsumer|\bcommerce\b|\bsales\b/ },
  { Icon: Buildings, color: '#b07d2b', re: /real estate|propert|housing|construction|\benterprise/ },
  { Icon: Factory, color: '#8d7b1f', re: /manufactur|\bindustr/ },
  { Icon: Bank, color: '#e11d48', re: /government|\bpolicy|regulat|\blaw\b|legal|legislat|congress|federal|politic|\btax\b|antitrust|interference/ },
  { Icon: Megaphone, color: '#c026d3', re: /marketing|\bbrand/ },
  { Icon: Database, color: '#9b59b6', re: /\bdata\b|analytics|scrap|dataset|database|\bcloud\b|infrastructure|\bsaas\b/ },
  { Icon: Robot, color: '#8b5cf6', re: /\ba\.?i\b|artificial intelligence|machine learning|\bml\b|\bllms?\b|gen-?ai|generative|chat-?bot|automat|autonomous|\bagents?\b|neural|deep learning/ },
  { Icon: TrendUp, color: '#15803d', re: /growth|expansion|expand|scal(e|es|ing)|momentum|\bsurge|\bboom\b|rising|uptrend|adoption/ },
  { Icon: Rocket, color: '#f97316', re: /launch|innovat|breakthrough|disrupt|emerging|next-gen|cutting-edge/ },
  { Icon: Globe, color: '#65a30d', re: /\bglobal|worldwide|international|geopolit/ },
  { Icon: ChatsCircle, color: '#ec4899', re: /communicat|conversation|messaging|\bcommunity|compassion/ },
  { Icon: Wrench, color: '#b5651d', re: /\btools?\b|platform|productivity|workflow|integration/ },
];

/** Neutral fallback when nothing matches — a generic "theme / idea". */
const FALLBACK: ThemeIcon = { Icon: Lightbulb, color: '#b8860b' };

/**
 * Pick a representative duotone icon + color for a theme from its
 * title/summary/angles. Title hits weigh more than body hits; earliest category
 * wins on a tie.
 */
export function themeIcon(theme: Pick<BriefTheme, 'title' | 'summary' | 'suggestedAngles'>): ThemeIcon {
  const title = (theme.title ?? '').toLowerCase();
  const rest = `${theme.summary ?? ''} ${(theme.suggestedAngles ?? []).join(' ')}`.toLowerCase();

  let best: ThemeIcon = FALLBACK;
  let bestScore = 0;
  for (const cat of CATEGORIES) {
    const score = (cat.re.test(title) ? 3 : 0) + (cat.re.test(rest) ? 1 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = { Icon: cat.Icon, color: cat.color };
    }
  }
  return best;
}
